import React, {createContext, useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import Honeybadger from '@honeybadger-io/js';
import CashApi from '../services/CashApi';
import noop from '../utils/noop';

const Context = createContext({
  isFetchingCashStatus: false,
  isUpdatingCashFund: false,
  cashRegistry: undefined,
  hasError: false,
  fetchCashStatus: noop,
  deactivateCashFund: noop,
  reactivateCashFund: noop,
});

export default Context;

export const CashFundContextProvider = ({children}) => {
  const [isUpdatingCashFund, setIsUpdatingCashFund] = useState(false);
  const [isFetchingCashStatus, setIsFetchingCashStatus] = useState(false);
  const [cashRegistry, setCashRegistry] = useState(undefined);
  const [hasError, setHasError] = useState(false);

  const fetchCashStatus = useCallback(async () => {
    try {
      setIsFetchingCashStatus(true);
      const cashRegistryResponse = await CashApi.getRegistry();

      /**
       * CashApi.getRegistry() resolves a 404 (no cash_registries row for this
       * member in api-cash) to `null` rather than throwing. When that happens,
       * cashRegistry is intentionally left at its initial `undefined` value —
       * this is what downstream consumers (e.g. Settings/index.js's
       * `hasCashFund`) rely on to silently hide cash-fund-related UI for
       * members who haven't set one up yet, instead of showing an error.
       */
      if (cashRegistryResponse) {
        setCashRegistry(cashRegistryResponse);
      }
    } catch (error) {
      setCashRegistry(undefined);
      setHasError(true);
      Honeybadger.notify(error, 'GetCashRegistryError');
    }
    setIsFetchingCashStatus(false);
  }, []);

  const deactivateCashFund = useCallback(async () => {
    setIsUpdatingCashFund(true);

    try {
      const response = await CashApi.updateRegistry({
        isActive: false,
      });
      setCashRegistry(response);
    } catch (error) {
      Honeybadger.notify(error, 'UpdateCashRegistryError');
    }

    setIsUpdatingCashFund(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reactivateCashFund = useCallback(async () => {
    setIsUpdatingCashFund(true);

    try {
      const response = await CashApi.updateRegistry({isActive: true});
      setCashRegistry(response);
    } catch (error) {
      Honeybadger.notify(error, 'UpdateCashRegistryError');
    }

    setIsUpdatingCashFund(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Context.Provider
      value={{
        isFetchingCashStatus,
        isUpdatingCashFund,
        cashRegistry,
        hasError,
        fetchCashStatus,
        deactivateCashFund,
        reactivateCashFund,
      }}>
      {children}
    </Context.Provider>
  );
};

CashFundContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
